import { FC, ReactNode, useEffect } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../lib/utils'

interface MobileSidebarDrawerProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
}

/**
 * Mobile sidebar drawer with overlay backdrop
 * Slides in from left, closes on backdrop click or X button
 */
export const MobileSidebarDrawer: FC<MobileSidebarDrawerProps> = ({
  isOpen,
  onClose,
  children
}) => {
  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 backdrop-blur-sm z-40',
          'transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={cn(
          'fixed left-0 top-0 h-full w-[280px] z-50',
          'bg-surface border-r border-border/50',
          'transform transition-transform duration-300 ease-out',
          'flex flex-col',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Sidebar navigation"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className={cn(
            'absolute right-2 top-2 z-10',
            'h-8 w-8 rounded-lg',
            'flex items-center justify-center',
            'text-muted-foreground hover:text-foreground',
            'hover:bg-accent/50',
            'transition-colors'
          )}
          aria-label="Close sidebar"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Drawer content */}
        <div className="flex-1 overflow-hidden">
          {children}
        </div>
      </div>
    </>
  )
}
